import { AUTHENTICATION_V2_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import { NUMBER_OF_TEAMS_PER_PAGE, TeamsOrderBy } from './components/TeamsTable/Teams.constants'
import { Team } from './FlowTeams.types'
import { UseTeamsArgs } from './components/TeamsTable/Teams.types'
import { getFetchTeamEndpoint, getTeamFromTeamType } from './FlowTeams.utils'
import { orgEndPoints } from '../../components/org/constants/orgAdmin'
import { useAuthState } from '../../hooks/useAuthState'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFetcher } from '../../components/org/hooks/useOrgHooks'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/system'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import useSWR, { useSWRConfig } from 'swr'

export interface Invitation {
  message: 'success-retrieval' | 'existing-member'
  linkType: 'ORG' | 'TEAM'
  orgName: string
  teamName: string | null
  teamId: string | null
}

export function useNameParts(
  name: string,
  substring?: string
): { text: string; highlight: boolean }[] {
  return useMemo(
    () => parse(name, match(name, substring ?? '', { insideWords: true })),
    [name, substring]
  )
}

export const useTeam = (
  teamId: string,
  orderBy: TeamsOrderBy
): {
  team: Team | undefined
  refreshTeam: () => void
  subteamsLoading: boolean
  subteams: Team[]
} => {
  const fetcher = useFetcher()
  const { jwt } = useAuthState()
  const orgId = jwt?.org?.orgId ?? ''
  const endpoint = getFetchTeamEndpoint({ orgId, teamId, orderBy })

  const { mutate } = useSWRConfig()
  const { data, isValidating } = useSWR(endpoint, orgId ? fetcher : async () => undefined, {
    revalidateOnMount: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true
  })

  const team = getTeamFromTeamType(data?.teams?.[0])

  //firstLoadSubteams allows accurate loading state + caching of initial list while data loads w new sorting params
  const [firstLoadSubteams, setFirstLoadSubteams] = useState<Team[] | null>(null)

  useEffect(() => {
    if (team?.directChildren && !firstLoadSubteams) {
      setFirstLoadSubteams(team?.directChildren)
    }
  }, [team, firstLoadSubteams])

  const subteams = team?.directChildren ?? firstLoadSubteams ?? []
  return {
    team,
    subteams,
    subteamsLoading: !firstLoadSubteams && isValidating,
    refreshTeam: () => mutate(endpoint)
  }
}

export const useTeams = ({
  limit = NUMBER_OF_TEAMS_PER_PAGE,
  offset = 0,
  orderBy = TeamsOrderBy.NameAsc,
  searchTerm,
  paginate = true,
  teamId,
  directChildren = false,
  topLevel = false,
  members = false
}: UseTeamsArgs): {
  teams: Team[] | undefined
  refreshTeams: () => void
  loading: boolean
} => {
  const { jwt } = useAuthState()
  const orgId = jwt?.org?.orgId ?? ''
  const queryStringObject: Record<string, string> = { orderBy }
  if (paginate) {
    queryStringObject.limit = limit.toString()
    queryStringObject.offset = offset.toString()
  }
  if (searchTerm) queryStringObject.searchTerm = searchTerm
  if (teamId) queryStringObject.teamId = teamId
  let queryString = new URLSearchParams(queryStringObject).toString()
  if (directChildren) queryString = queryString + '&directChildren'
  if (topLevel) queryString = queryString + '&topLevel'
  if (members) queryString = queryString + '&members'

  const endpoint = orgId ? `${orgEndPoints.teams}/${orgId}?${queryString}` : null

  const fetcher = useFetcher()

  const { mutate } = useSWRConfig()
  const { data, isValidating } = useSWR(endpoint, orgId ? fetcher : async () => undefined, {
    revalidateOnMount: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true
  })

  const [teamsHaveLoaded, setTeamsHaveLoaded] = useState(false)

  useEffect(() => {
    if (data?.teams) {
      setTeamsHaveLoaded(true)
    }
  }, [data])

  const loading = !teamsHaveLoaded && isValidating

  return {
    loading,
    teams: data?.teams,
    refreshTeams: () => mutate(endpoint)
  }
}

export function useShowMobileStyles(): boolean {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('md'))
}

type AcceptInvitationRequest = (
  inviteId?: string,
  invitation?: Invitation
) => Promise<{ success: boolean }>

export function useAcceptInvitationRequest(): AcceptInvitationRequest {
  const { token } = useAuthState()

  return useCallback<AcceptInvitationRequest>(
    async (inviteId, invitation) => {
      if (!inviteId || !invitation) return { success: false }
      const response = await fetch(
        `${AUTHENTICATION_V2_ENDPOINT}/invitation-link/${inviteId}/join`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      return { success: response.ok ?? false }
    },
    [token]
  )
}
